import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import EmpresaDesativada from "@/pages/EmpresaDesativada";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: "master" | "admin" | "user";
  redirectTo?: string;
}

/**
 * Component to protect routes that require authentication
 * Optionally checks for specific role
 * Also checks if empresa is active (except for master users)
 */
export function ProtectedRoute({
  children,
  requiredRole,
  redirectTo = "/auth/login",
}: ProtectedRouteProps) {
  const { user, profile, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check if empresa is active (master users are always allowed)
  if (profile && profile.role !== "master") {
    // Get empresa info from profile
    const empresa = profile.empresas;
    
    // Check if empresa is inactive
    if (empresa && (empresa.is_active === false || empresa.status === "inativa")) {
      return <EmpresaDesativada empresaNome={empresa.nome || "sua empresa"} />;
    }
  }

  if (requiredRole && profile) {
    const roleHierarchy: Record<string, number> = {
      user: 1,
      admin: 2,
      master: 3,
    };

    const userRoleLevel = roleHierarchy[profile.role] || 0;
    const requiredRoleLevel = roleHierarchy[requiredRole] || 0;

    if (userRoleLevel < requiredRoleLevel) {
      // User doesn't have required role, redirect to their dashboard
      if (profile.role === "master") {
        return <Navigate to="/dashboard/master" replace />;
      } else if (profile.role === "admin") {
        return <Navigate to="/dashboard/admin" replace />;
      } else {
        return <Navigate to="/dashboard/colaborador" replace />;
      }
    }
  }

  return <>{children}</>;
}

